import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Heart, ArrowLeft, Bell, X, Bookmark } from "lucide-react";

const POST_PREF_LABELS = {
  post_updated: "Beitrag aktualisiert",
  post_deleted: "Beitrag entfernt",
};

export default function SavedPostsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefsFor, setPrefsFor] = useState(null);
  const [unreadByTarget, setUnreadByTarget] = useState({});
  const [area, setArea] = useState("SOCIAL");
  const [likedIds, setLikedIds] = useState(() => new Set());

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/me/watch-notifications/summary");
      setUnreadByTarget(data.by_target || {});
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/profile/saved-posts?area=${area}`);
      const list = data.posts || [];
      setPosts(list);
      setLikedIds(new Set(list.filter((p) => p.liked).map((p) => p.target_id)));
    } catch { setPosts([]); }
    finally { setLoading(false); }
  }, [user, area]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
    loadUnread();
  }, [user, navigate, load, loadUnread]);

  const unwatch = async (post) => {
    try {
      await api.post(`/posts/${post.target_id}/watch`);
      setPosts(prev => prev.filter(p => p.target_id !== post.target_id));
    } catch { /* ignore */ }
  };

  const toggleLike = async (post) => {
    const isLiked = likedIds.has(post.target_id);
    setLikedIds((prev) => {
      const nx = new Set(prev);
      if (isLiked) nx.delete(post.target_id); else nx.add(post.target_id);
      return nx;
    });
    try {
      if (isLiked) await api.delete(`/posts/${post.target_id}/like`);
      else await api.post(`/posts/${post.target_id}/like`);
    } catch {
      setLikedIds((prev) => { const nx = new Set(prev); if (isLiked) nx.add(post.target_id); else nx.delete(post.target_id); return nx; });
    }
  };

  const togglePref = async (post, key, currentValue) => {
    try {
      const { data } = await api.patch(`/me/watches/${post.target_id}/preferences`, {
        preferences: { [key]: !currentValue },
      });
      setPosts(prev => prev.map(p => p.target_id === post.target_id ? { ...p, watch_preferences: data.preferences } : p));
    } catch { /* ignore */ }
  };

  const openPost = async (post) => {
    try {
      await api.post(`/me/watch-notifications/mark-read/${post.target_id}`);
      setUnreadByTarget(prev => { const nx = { ...prev }; delete nx[post.target_id]; return nx; });
      window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
    } catch { /* ignore */ }
    navigate(`/post/${post.target_id}`);
  };

  return (
    <div data-testid="saved-posts-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="saved-posts-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex-1">{t("saved_posts.title")}</h1>
        <span className="text-xs text-[#999]">{posts.length}</span>
      </div>

      {/* Bereichs-Untertabs: Social / stilltouchedX */}
      <div data-testid="saved-posts-area-tabs" className="flex gap-1 p-1 mx-4 mt-3 rounded-full bg-[#F0F0F0] w-fit">
        {[{ k: "SOCIAL", l: "Social" }, { k: "STILLTOUCHEDX", l: "stilltouchedX" }].map((tab) => (
          <button key={tab.k} data-testid={`saved-posts-tab-${tab.k.toLowerCase()}`} onClick={() => setArea(tab.k)}
            className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${area === tab.k ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:text-[#0A0A0A]"}`}>
            {tab.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <Heart size={32} className="mx-auto text-[#E5E5E5] mb-3" />
          <p className="text-sm text-[#999]">{t("saved_posts.empty")}</p>
          <p className="text-[11px] text-[#BBB] mt-2">{t("saved_posts.hint")}</p>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {posts.map((post) => {
            const prefs = post.watch_preferences || { post_updated: true, post_deleted: true };
            const isPrefsOpen = prefsFor === post.target_id;
            const badge = unreadByTarget[post.target_id];
            return (
              <div key={post.target_id} data-testid={`saved-post-${post.target_id}`}
                className="relative flex gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                onClick={() => openPost(post)}>
                <div className="w-16 h-20 bg-[#F5F5F5] shrink-0 overflow-hidden relative">
                  {(post.media_urls?.[0] || post.media_url) ? (
                    <img src={post.media_urls?.[0] || post.media_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-[#CCC]">Text-Post</div>
                  )}
                  {badge > 0 && (
                    <span data-testid={`saved-post-badge-${post.target_id}`}
                      className="absolute -top-1 -left-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{post.creator_display_name || "@" + (post.creator_handle || "unbekannt")}</p>
                  <p className="text-xs text-[#666] mt-0.5 line-clamp-2">{post.text || post.title || "—"}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0 self-center">
                  <button data-testid={`saved-post-prefs-${post.target_id}`}
                    onClick={(e) => { e.stopPropagation(); setPrefsFor(isPrefsOpen ? null : post.target_id); }}
                    aria-label="Benachrichtigungen"
                    className="p-1 text-[#666] hover:text-[#0A0A0A]">
                    <Bell size={16} />
                  </button>
                  {/* Herz = NUR Like (an/aus), unabhängig vom Speichern */}
                  <button data-testid={`saved-post-like-${post.target_id}`}
                    onClick={(e) => { e.stopPropagation(); toggleLike(post); }}
                    aria-label={likedIds.has(post.target_id) ? "Like entfernen" : "Liken"}
                    className="p-1 text-[#0A0A0A]">
                    <Heart size={18} className={likedIds.has(post.target_id) ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-[#999]"} />
                  </button>
                  {/* Lesezeichen = aus Gespeicherten entfernen */}
                  <button data-testid={`saved-post-remove-${post.target_id}`}
                    onClick={(e) => { e.stopPropagation(); unwatch(post); }}
                    aria-label="Aus Gespeicherten entfernen"
                    className="p-1 text-[#0A0A0A]">
                    <Bookmark size={18} className="fill-[#0A0A0A]" />
                  </button>
                </div>
                {isPrefsOpen && (
                  <div
                    data-testid={`saved-post-prefs-popover-${post.target_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-4 top-14 w-64 bg-white border border-[#E5E5E5] shadow-lg z-10 rounded">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
                      <p className="text-xs font-medium text-[#0A0A0A]">Benachrichtigen wenn:</p>
                      <button onClick={() => setPrefsFor(null)} className="text-[#999] hover:text-[#0A0A0A]"><X size={14} /></button>
                    </div>
                    <div className="divide-y divide-[#F0F0F0]">
                      {Object.entries(POST_PREF_LABELS).map(([key, label]) => (
                        <label key={key} className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-[#FAFAFA]">
                          <span className="text-xs text-[#333]">{label}</span>
                          <input
                            data-testid={`saved-post-prefs-${key}-${post.target_id}`}
                            type="checkbox"
                            checked={!!prefs[key]}
                            onChange={() => togglePref(post, key, prefs[key])}
                            className="w-4 h-4 accent-[#0A0A0A]"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
